import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  HospitalDetailsDTO,
  HospitalUpdateDTO,
  HospitalWithTotalsDTO,
  UpdateDepartmentsWithWardsRequest,
  UpsertLinkedHospitalsDTO,
  UserDTO
} from '@/generated'
import { useNoticeStore } from './notice'
import { NoticeType } from './models/Notice'
import { AdminHospitalService } from '@/hospitalAdminTool/services/AdminHospitalService'
import i18n from '@/i18n/i18n'

export const useHospitalsStore = defineStore('hospitals', () => {
  const hospitals = ref<HospitalWithTotalsDTO[]>([])
  const totalItems = ref(0)
  const isHospitalsStoreLoading = ref(false)
  const currentHospital = ref<HospitalDetailsDTO | null>(null)
  const isLoadingCurrentHospital = ref(false)
  const availableHospitalAdminCandidates = ref<UserDTO[]>([])
  const isAvailableHospitalAdminCandidatesLoading = ref(false)
  const isLinkedHospitalsStoreLoading = ref(false)

  const isHospitalsStoreInitialLoading = computed(() => {
    return isHospitalsStoreLoading.value && hospitals.value.length === 0
  })

  const error = ref<string | null>(null)
  const noticeStore = useNoticeStore()

  const hospitalIdToName = computed(() => {
    const map = new Map<string, string>()

    if (hospitals.value.length === 0) {
      fetchHospitals(0, 100)
    }

    for (const hospital of hospitals.value) {
      map.set(hospital.id, hospital.name)
    }
    return map
  })

  function setHospitals(hospitalsList: HospitalWithTotalsDTO[]) {
    hospitals.value = hospitalsList
  }

  async function saveLinkedHospitalsForHospitalId(hospitalId: string, linkedHospitals: string[]) {
    try {
      const dto: UpsertLinkedHospitalsDTO = {
        linkedHospitalIds: linkedHospitals
      }
      await AdminHospitalService.updateLinkedHospitals(hospitalId, dto)
      await fetchLinkedHospitalsForHospitalId(hospitalId)
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to save linked hospitals')
      })
    }
  }

  async function fetchLinkedHospitalsForHospitalId(hospitalId: string) {
    try {
      isLinkedHospitalsStoreLoading.value = true
      const response = await AdminHospitalService.getLinkedHospitals(hospitalId)
      return response.items
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to load linked hospitals')
      })
      return []
    } finally {
      isLinkedHospitalsStoreLoading.value = false
    }
  }

  async function fetchHospitals(offset: number, limit: number) {
    try {
      isHospitalsStoreLoading.value = true
      const response = await AdminHospitalService.getHospitals(offset, limit)
      hospitals.value = response.items
      totalItems.value = response.total
      return response.items
    } catch (error) {
      hospitals.value = []
      totalItems.value = 0
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to load hospitals')
      })
      return []
    } finally {
      isHospitalsStoreLoading.value = false
    }
  }

  async function refreshHospitalById(hospitalId: string) {
    isLoadingCurrentHospital.value = true

    try {
      const hospital = await AdminHospitalService.getHospitalById(hospitalId)
      currentHospital.value = hospital
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Hospital not found'
      })
      return
    } finally {
      isLoadingCurrentHospital.value = false
    }
  }

  async function refreshHospitalAdminCandidates(hospitalId: string) {
    isAvailableHospitalAdminCandidatesLoading.value = true

    try {
      const candidatesData = await AdminHospitalService.getHospitalAdminCandidates(hospitalId)
      availableHospitalAdminCandidates.value = candidatesData.hospitalAdmins
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Hospital admin candidates not found'
      })
      return
    } finally {
      isAvailableHospitalAdminCandidatesLoading.value = false
    }
  }

  async function updateHospital(hospitalId: string, hospital: HospitalUpdateDTO) {
    try {
      await AdminHospitalService.updateHospital(hospitalId, hospital)
      noticeStore.add({
        type: NoticeType.SUCCESS,
        message: i18n.global.t('Hospital updated successfully')
      })
      await refreshHospitalById(hospitalId)
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to update hospital')
      })
    }
  }

  async function updateHospitalAdmins(hospitalId: string, hospitalAdmins: string[]) {
    try {
      await AdminHospitalService.updateHospitalAdmins(hospitalId, hospitalAdmins)
      noticeStore.add({
        type: NoticeType.SUCCESS,
        message: i18n.global.t('Hospital admins updated successfully')
      })
      await refreshHospitalById(hospitalId)
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to update hospital admins')
      })
    }
  }

  async function addDepartmentsToHospital(hospitalId: string, departmentNames: string[]) {
    try {
      await AdminHospitalService.addDepartmentsToHospital(hospitalId, departmentNames)
      noticeStore.add({
        type: NoticeType.SUCCESS,
        message: i18n.global.t('Department(s) created')
      })
      await refreshHospitalById(hospitalId)
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to add department(s)')
      })
    }
  }

  async function addWardsToDepartment(
    hospitalId: string,
    departmentId: string,
    wardNames: string[]
  ) {
    try {
      await AdminHospitalService.addWardsToDepartment(hospitalId, departmentId, wardNames)
      noticeStore.add({
        type: NoticeType.SUCCESS,
        message: i18n.global.t('Ward created')
      })
      await refreshHospitalById(hospitalId)
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to add ward')
      })
    }
  }

  async function updateHospitalDepartmentsAndWards(
    hospitalId: string,
    updateDepartmentsWithWardsRequest: UpdateDepartmentsWithWardsRequest
  ) {
    try {
      await AdminHospitalService.updateHospitalDepartmentsAndWards(
        updateDepartmentsWithWardsRequest
      )
      noticeStore.add({
        type: NoticeType.SUCCESS,
        message: i18n.global.t('Changes saved')
      })
      await refreshHospitalById(hospitalId)
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to save changes')
      })
    }
  }

  return {
    hospitals,
    currentHospital,
    totalItems,
    isHospitalsStoreInitialLoading,
    isHospitalsStoreLoading,
    isLoadingCurrentHospital,
    isLinkedHospitalsStoreLoading,
    error,
    setHospitals,
    hospitalIdToName,
    fetchHospitals,
    refreshHospitalById,
    updateHospital,
    updateHospitalAdmins,
    addDepartmentsToHospital,
    addWardsToDepartment,
    updateHospitalDepartmentsAndWards,
    refreshHospitalAdminCandidates,
    availableHospitalAdminCandidates,
    isAvailableHospitalAdminCandidatesLoading,
    fetchLinkedHospitalsForHospitalId,
    saveLinkedHospitalsForHospitalId
  }
})
